import React, { useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, List, ListItemText, ListItemIcon, ListItemButton, Avatar, LinearProgress, Chip } from '@mui/material';
import { AccountBalance as MoneyIcon, Savings as SavingsIcon, Receipt as ExpenseIcon, SwapHoriz as PaymentIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageHeader, EmptyState, LoadingSpinner, DataCard } from '../../components/common';
import { AllowanceFormDialog, ExpenseFormDialog, SavingsGoalFormDialog, PaymentFormDialog } from '../../components/dialogs';
import { useFamilyData } from '../../hooks';
import { useFamily } from '../../context';
import { colors } from '../../theme';

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const MoneyPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { getMemberName, getMemberColor, stats } = useFamily();

  // Data hooks
  const { items: allowances, loading: allowancesLoading, insert: insertAllowance, update: updateAllowance, remove: removeAllowance } = useFamilyData('allowance');
  const { items: expenses, loading: expensesLoading, insert: insertExpense, update: updateExpense, remove: removeExpense } = useFamilyData('expense');
  const { items: savingsGoals, loading: savingsLoading, insert: insertSavings, update: updateSavings, remove: removeSavings } = useFamilyData('savings_goal');
  const { items: payments, loading: paymentsLoading, insert: insertPayment, update: updatePayment, remove: removePayment } = useFamilyData('payment');

  // Dialog states
  const [allowanceDialogOpen, setAllowanceDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [savingsDialogOpen, setSavingsDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedAllowance, setSelectedAllowance] = useState<any>(null);
  const [selectedExpense, setSelectedExpense] = useState<any>(null);
  const [selectedSavings, setSelectedSavings] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);

  const loading = allowancesLoading || expensesLoading || savingsLoading || paymentsLoading;

  // Allowance handlers
  const handleSaveAllowance = async (data: any, familyIds: string[]) => {
    try {
      if (selectedAllowance?.id) {
        await updateAllowance(selectedAllowance.id, data);
        enqueueSnackbar('Allowance updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) {
          await insertAllowance(data, familyId);
        }
        enqueueSnackbar('Allowance added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save allowance', { variant: 'error' });
      throw err;
    }
  };

  // Expense handlers
  const handleSaveExpense = async (data: any, familyIds: string[]) => {
    try {
      if (selectedExpense?.id) {
        await updateExpense(selectedExpense.id, data);
        enqueueSnackbar('Expense updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) {
          await insertExpense(data, familyId);
        }
        enqueueSnackbar('Expense added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save expense', { variant: 'error' });
      throw err;
    }
  };

  // Savings handlers
  const handleSaveSavings = async (data: any, familyIds: string[]) => {
    try {
      if (selectedSavings?.id) {
        await updateSavings(selectedSavings.id, data);
        enqueueSnackbar('Savings goal updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) {
          await insertSavings(data, familyId);
        }
        enqueueSnackbar('Savings goal added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save savings goal', { variant: 'error' });
      throw err;
    }
  };

  // Payment handlers
  const handleSavePayment = async (data: any, familyIds: string[]) => {
    try {
      if (selectedPayment?.id) {
        await updatePayment(selectedPayment.id, data);
        enqueueSnackbar('Payment updated', { variant: 'success' });
      } else {
        for (const familyId of familyIds) {
          await insertPayment(data, familyId);
        }
        enqueueSnackbar('Payment added', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save payment', { variant: 'error' });
      throw err;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading money data..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Money"
        subtitle="Manage allowances, expenses, payments, and savings"
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Allowances"
            icon={MoneyIcon}
            iconColor={colors.money}
            value={allowances.length}
            onClick={() => {
              setSelectedAllowance(null);
              setAllowanceDialogOpen(true);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Expenses"
            icon={ExpenseIcon}
            iconColor="#F44336"
            value={expenses.length}
            onClick={() => {
              setSelectedExpense(null);
              setExpenseDialogOpen(true);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Payments"
            icon={PaymentIcon}
            iconColor="#2196F3"
            value={payments.length}
            onClick={() => {
              setSelectedPayment(null);
              setPaymentDialogOpen(true);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Savings Goals"
            icon={SavingsIcon}
            iconColor="#4CAF50"
            value={savingsGoals.length}
            onClick={() => {
              setSelectedSavings(null);
              setSavingsDialogOpen(true);
            }}
          />
        </Grid>

        {/* Owed Summary */}
        {(stats?.tally?.owedByMe || stats?.tally?.owedToMe) ? (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                    I Owe
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {formatCurrency(stats?.tally?.owedByMe || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                    Owed to Me
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {formatCurrency(stats?.tally?.owedToMe || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        ) : null}

        {/* Allowances */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Allowances</Typography>
              </Box>

              {allowances.length === 0 ? (
                <EmptyState
                  icon={MoneyIcon}
                  title="No allowances"
                  description="Set up recurring allowances for family members"
                  actionLabel="Add Allowance"
                  onAction={() => {
                    setSelectedAllowance(null);
                    setAllowanceDialogOpen(true);
                  }}
                />
              ) : (
                <List disablePadding>
                  {allowances.map((item) => {
                    const data = (item.data?.data || {}) as any;
                    return (
                      <ListItemButton
                        key={item.id}
                        sx={{ px: 0, borderRadius: 1 }}
                        onClick={() => {
                          setSelectedAllowance(item);
                          setAllowanceDialogOpen(true);
                        }}
                      >
                        {data.personId && (
                          <ListItemIcon>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: getMemberColor(data.personId), fontSize: '0.75rem' }}>
                              {getMemberName(data.personId).charAt(0)}
                            </Avatar>
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={data.personId ? getMemberName(data.personId) : 'Allowance'}
                          secondary={`${data.frequency || 'Weekly'}${data.dayOfWeek ? ` - ${data.dayOfWeek}` : ''}`}
                        />
                        <Typography variant="h6" color="primary.main">
                          ${data.amount || 0}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Savings Goals */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Savings Goals</Typography>
              </Box>

              {savingsGoals.length === 0 ? (
                <EmptyState
                  icon={SavingsIcon}
                  title="No savings goals"
                  description="Create savings goals to track progress"
                  actionLabel="Add Goal"
                  onAction={() => {
                    setSelectedSavings(null);
                    setSavingsDialogOpen(true);
                  }}
                />
              ) : (
                <List disablePadding>
                  {savingsGoals.map((item) => {
                    const data = (item.data?.data || {}) as any;
                    const target = data.targetAmount || data.target || 0;
                    const current = data.currentAmount || data.saved || 0;
                    const progress = target ? (current / target) * 100 : 0;
                    return (
                      <ListItemButton
                        key={item.id}
                        sx={{ px: 0, borderRadius: 1 }}
                        onClick={() => {
                          setSelectedSavings(item);
                          setSavingsDialogOpen(true);
                        }}
                      >
                        <ListItemText
                          primary={data.name || 'Savings Goal'}
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption">${current} of ${target}</Typography>
                                <Typography variant="caption">{Math.round(progress)}%</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={Math.min(progress, 100)} color={progress >= 100 ? 'success' : 'primary'} />
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payments */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Payments</Typography>
              </Box>

              {payments.length === 0 ? (
                <EmptyState
                  icon={PaymentIcon}
                  title="No payments"
                  description="Record payments between family members"
                  actionLabel="Add Payment"
                  onAction={() => {
                    setSelectedPayment(null);
                    setPaymentDialogOpen(true);
                  }}
                />
              ) : (
                <List disablePadding>
                  {payments.slice(0, 10).map((item) => {
                    const data = (item.data?.data || {}) as any;
                    const fromName = data.fromMemberId ? getMemberName(data.fromMemberId) : 'Unknown';
                    const toName = data.toMemberId ? getMemberName(data.toMemberId) : 'Unknown';
                    return (
                      <ListItemButton
                        key={item.id}
                        sx={{ px: 0, borderRadius: 1 }}
                        onClick={() => {
                          setSelectedPayment(item);
                          setPaymentDialogOpen(true);
                        }}
                      >
                        <ListItemText
                          primary={`${fromName} → ${toName}`}
                          secondary={data.date || data.notes || ''}
                        />
                        <Typography variant="body1" color="primary.main" fontWeight={500}>
                          ${data.amount || 0}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Expenses */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Recent Expenses</Typography>
              </Box>

              {expenses.length === 0 ? (
                <EmptyState
                  icon={ExpenseIcon}
                  title="No expenses"
                  description="Track family expenses"
                  actionLabel="Add Expense"
                  onAction={() => {
                    setSelectedExpense(null);
                    setExpenseDialogOpen(true);
                  }}
                />
              ) : (
                <List disablePadding>
                  {expenses.slice(0, 10).map((item) => {
                    const data = (item.data?.data || {}) as any;
                    return (
                      <ListItemButton
                        key={item.id}
                        sx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 1 }}
                        onClick={() => {
                          setSelectedExpense(item);
                          setExpenseDialogOpen(true);
                        }}
                      >
                        <ListItemText
                          primary={data.name || data.description || 'Expense'}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              {data.category && <Chip label={data.category} size="small" variant="outlined" />}
                              {data.date && <Typography variant="caption" color="text.secondary">{data.date}</Typography>}
                            </Box>
                          }
                        />
                        <Typography variant="body1" color="error.main" fontWeight={500}>
                          -${data.amount || 0}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <AllowanceFormDialog
        open={allowanceDialogOpen}
        onClose={() => { setAllowanceDialogOpen(false); setSelectedAllowance(null); }}
        onSave={handleSaveAllowance}
        onDelete={selectedAllowance ? async () => {
          try { await removeAllowance(selectedAllowance.id); enqueueSnackbar('Allowance deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete allowance', { variant: 'error' }); throw err; }
        } : undefined}
        allowance={selectedAllowance}
      />

      <ExpenseFormDialog
        open={expenseDialogOpen}
        onClose={() => { setExpenseDialogOpen(false); setSelectedExpense(null); }}
        onSave={handleSaveExpense}
        onDelete={selectedExpense ? async () => {
          try { await removeExpense(selectedExpense.id); enqueueSnackbar('Expense deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete expense', { variant: 'error' }); throw err; }
        } : undefined}
        expense={selectedExpense}
      />

      <SavingsGoalFormDialog
        open={savingsDialogOpen}
        onClose={() => { setSavingsDialogOpen(false); setSelectedSavings(null); }}
        onSave={handleSaveSavings}
        onDelete={selectedSavings ? async () => {
          try { await removeSavings(selectedSavings.id); enqueueSnackbar('Savings goal deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete savings goal', { variant: 'error' }); throw err; }
        } : undefined}
        savingsGoal={selectedSavings}
      />

      <PaymentFormDialog
        open={paymentDialogOpen}
        onClose={() => { setPaymentDialogOpen(false); setSelectedPayment(null); }}
        onSave={handleSavePayment}
        onDelete={selectedPayment ? async () => {
          try { await removePayment(selectedPayment.id); enqueueSnackbar('Payment deleted', { variant: 'success' }); }
          catch (err) { enqueueSnackbar('Failed to delete payment', { variant: 'error' }); throw err; }
        } : undefined}
        payment={selectedPayment}
      />
    </Box>
  );
};

export default MoneyPage;
